import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUserInfo } = useAuth();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const [errorType, setErrorType] = useState('');

  useEffect(() => {
    let isMounted = true; // Prevent state updates after unmount
    
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        if (isMounted) {
          setStatus('error');
          setMessage('Verification token is missing from the URL.');
          setErrorType('no_token');
        }
        return;
      }

      try {
        const response = await axios.get(buildApiUrl('/auth/verify-email/'), {
          params: { token }
        });

        if (isMounted) {
          setStatus('success');
          setMessage(response.data.message || 'Email verified successfully!');
          
          // Refresh user info if logged in
          if (user) {
            await refreshUserInfo();
          }

          // Redirect after 3 seconds
          setTimeout(() => {
            if (isMounted) {
              navigate('/extraction');
            }
          }, 3000);
        }

      } catch (error) {
        if (isMounted) {
          setStatus('error');
          
          if (error.response?.data?.error) {
            setMessage(error.response.data.error);
            
            // Determine error type
            if (error.response.data.error.includes('expired')) {
              setErrorType('expired');
            } else if (error.response.data.error.includes('used')) {
              setErrorType('used');
            } else if (error.response.data.error.includes('Invalid')) {
              setErrorType('invalid');
            }
          } else {
            setMessage('Email verification failed. Please try again.');
          }
        }
      }
    };

    verifyEmail();
    
    return () => {
      isMounted = false; // Cleanup on unmount
    };
  }, [searchParams, navigate, user, refreshUserInfo]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-slate-700/50 rounded-xl p-8 border border-slate-600/50">
          {status === 'verifying' && (
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mb-4"></div>
              <h2 className="text-3xl font-bold text-white mb-2">Verifying your email...</h2>
              <p className="text-gray-300">Please wait while we verify your email address.</p>
            </div>
          )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-500/20 mb-4">
              <svg className="h-6 w-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Email Verified!</h2>
            <p className="text-gray-300 mb-4">{message}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-500/20 mb-4">
              <svg className="h-6 w-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">Verification Failed</h2>
            <p className="text-gray-300 mb-6">{message}</p>

            <div className="space-y-3">
              {(errorType === 'expired' || errorType === 'used') && user && !user.is_verified && (
                <Link
                  to="/resend-verification"
                  className="block w-full py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  Request New Verification Email
                </Link>
              )}
              
              {!user && (
                <Link
                  to="/login"
                  className="block w-full py-3 px-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors duration-200"
                >
                  Go to Login
                </Link>
              )}
              
              <Link
                to="/"
                className="block w-full py-3 px-4 bg-slate-600 hover:bg-slate-500 text-white font-semibold rounded-lg transition-colors duration-200"
              >
                Go to Home
              </Link>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
